// Реквизит «Профиль аватара (Диалог)» — общий для настроек пользователя и
// селектора на форме Диалога. Живёт в shared/, потому что нужен обеим сторонам,
// а фича Диалога и композиционный корень (main.ts) друг друга не импортируют.
import { state } from './state';

/** Чего не хватает для платного профиля: регистрации с предоплатой или средств. */
export type AvatarProfileBlockReason = 'registration_required' | 'insufficient_funds';

export type AvatarProfileOption = {
  avatar_id: string;
  label: string;
  /** Категория Диалога профиля; пусто — профиль её не задаёт. */
  category?: string;
  /** Профиль тратит платные ресурсы (premium/облачный TTS/платная категория). */
  requires_credits?: boolean;
  /** Индекс сортировки из настроек профиля: сервер уже упорядочил список по нему. */
  sort_index?: number;
  /** Можно ли выбрать сейчас. */
  available?: boolean;
  /** Заполнено только когда available=false. */
  unavailable_reason?: AvatarProfileBlockReason;
};

/** Подсказка под селектом: почему платные профили сейчас недоступны. */
export function avatarProfileBlockHint(profiles: AvatarProfileOption[]): string {
  const blocked = profiles.find((p) => p.available === false);
  if (!blocked) return '';
  return blocked.unavailable_reason === 'registration_required'
    ? 'Аватары на платных ресурсах доступны после регистрации и предоплаты.'
    : 'Аватары на платных ресурсах доступны при наличии кредитов на балансе — пополните счёт или запросите кредит у администратора.';
}

function authHeaders(json = false): Record<string, string> {
  const headers: Record<string, string> = { Authorization: 'Bearer ' + state.authToken };
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

/**
 * Все профили аватара с признаком доступности. Список одинаков во всех местах,
 * откуда задаётся поле: ограничений на смену нет, единственный гейт — кредиты
 * для профилей на платных сервисах.
 */
export async function fetchAvatarProfiles(): Promise<AvatarProfileOption[]> {
  try {
    const r = await fetch('/api/avg-dialog/avatar-profiles', { headers: authHeaders() });
    if (!r.ok) return [];
    const data = await r.json() as AvatarProfileOption[];
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/**
 * Назначает профиль текущему пользователю. Пустая строка = сбросить.
 * Платный профиль без средств возвращается кодом `registration_required` или
 * `insufficient_funds` — вызывающий показывает форму регистрации либо просто
 * сообщение.
 */
export async function saveAvatarProfile(
  avatarId: string
): Promise<{ ok: true } | { ok: false; error: string; code?: AvatarProfileBlockReason | string }> {
  try {
    const r = await fetch('/api/avg-dialog/avatar-profile', {
      method: 'PUT',
      headers: authHeaders(true),
      body: JSON.stringify({ avatar_id: avatarId || null }),
    });
    if (r.ok) return { ok: true };
    const data = await r.json().catch(() => ({})) as {
      detail?: string;
      error?: { message?: string; code?: string };
    };
    return {
      ok: false,
      error: data.error?.message || data.detail || 'Не удалось сохранить аватар',
      code: data.error?.code,
    };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

function escapeHtml(text: string): string {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Заполняет `<select>` профилями. Пустой опции «не задан» здесь нет: аватар в
 * Диалоге нужен всегда, а сбросить назначение может только админ в своей форме.
 *
 * Платный профиль без кредитов остаётся в списке, но выключен и подписан — что
 * именно нужно сделать (пополнить или зарегистрироваться), а не просто исчезает.
 *
 * Уже назначенный профиль показывается всегда, даже если сейчас недоступен
 * (баланс ушёл в ноль) или пропал из конфига: иначе селект молча показал бы
 * другое значение, а первое же сохранение затёрло бы назначение.
 */
export function fillAvatarProfileSelect(
  select: HTMLSelectElement,
  profiles: AvatarProfileOption[],
  selectedId: string
): void {
  const known = profiles.some((p) => p.avatar_id === selectedId);
  const missing = selectedId && !known
    ? `<option value="${escapeHtml(selectedId)}" selected>${escapeHtml(selectedId)} (аватар не найден)</option>`
    : '';
  select.innerHTML = missing + profiles
    .map((p) => {
      const blocked = p.available === false && p.avatar_id !== selectedId;
      const suffix = blocked
        ? (p.unavailable_reason === 'registration_required' ? ' — нужна регистрация' : ' — нужны кредиты')
        : '';
      return `<option value="${escapeHtml(p.avatar_id)}"${p.avatar_id === selectedId ? ' selected' : ''}${blocked ? ' disabled' : ''}>${escapeHtml(p.label)}${suffix}</option>`;
    })
    .join('');
  select.value = selectedId;
}
